/**
 * Grand Quack Auto — movement system.
 *
 * Reads world.input.move (normalized 2D), applies DUCK.walkSpeed to the duck's
 * position in camera-relative forward/right on the XZ plane, and lerps the
 * duck's rotation toward the movement heading via DUCK.turnLerp.
 *
 * Camera-relative means W always moves the duck toward where the camera is
 * looking, so the controls feel natural as the chase cam orbits.
 *
 * See research.md §5 session 2 prompt.
 */

import * as CANNON from 'cannon-es';
import * as THREE from 'three';
import { DUCK } from '../config';
import type { System, World } from '../types';

// Reusable vectors — avoid per-frame allocation.
const _forward = new THREE.Vector3();
const _right = new THREE.Vector3();
const _direction = new THREE.Vector3();

// ── Floppy Duck ragdoll mode ──────────────────────────────────────────────

let ragdollActive = false;
let ragdollEnd = 0;
let ragdollVelY = 0;
const RAGDOLL_DURATION = 3;
const BOUNCE = 0.7; // restitution — rubber duck bounce!

export const movementSystem: System = {
  name: 'movement',
  fixedUpdate(dt: number, world: World) {
    if (world.playerMode === 'driving') return;

    const duckBody = world.physics?.duckBody;

    // R key toggles floppy ragdoll mode.
    if (world.input.ragdollToggle) {
      if (!ragdollActive) {
        ragdollActive = true;
        ragdollEnd = world.time.elapsed + RAGDOLL_DURATION;
        ragdollVelY = 8; // launch up
        world.duck.rotation.set(
          Math.random() * 2 - 1,
          world.duck.rotation.y,
          Math.random() * 2 - 1,
        );
        // Park the body during ragdoll so it stops colliding/drifting.
        if (duckBody) {
          duckBody.velocity.set(0, 0, 0);
          duckBody.sleep();
        }
      }
    }

    // Ragdoll physics — duck bounces like a bath toy.
    if (ragdollActive) {
      ragdollVelY -= 20 * dt; // gravity
      world.duck.position.y += ragdollVelY * dt;
      // Tumble rotation for comedy.
      world.duck.rotation.x += 5 * dt;
      world.duck.rotation.z += 3 * dt;
      // Bounce off ground.
      if (world.duck.position.y <= 0) {
        world.duck.position.y = 0;
        ragdollVelY = -ragdollVelY * BOUNCE;
        if (Math.abs(ragdollVelY) < 0.5) ragdollVelY = 0;
      }
      if (world.time.elapsed > ragdollEnd) {
        ragdollActive = false;
        world.duck.position.y = 0;
        world.duck.rotation.x = 0;
        world.duck.rotation.z = 0;
        if (duckBody) {
          // Defensive: shapes[] could be empty mid-frame during an evolution
          // shape swap. Fall back to the configured radius in that case.
          const shape = duckBody.shapes.length > 0 ? duckBody.shapes[0] : null;
          const radius = shape instanceof CANNON.Sphere ? shape.radius : DUCK.bodyRadius;
          duckBody.position.set(world.duck.position.x, radius, world.duck.position.z);
          duckBody.velocity.set(0, 0, 0);
          duckBody.wakeUp();
        }
      }
      return; // skip normal movement while ragdolling
    }

    const { x, y } = world.input.move;
    if (x === 0 && y === 0) {
      // No input — stop the body so damping doesn't coast us into walls.
      if (duckBody) { duckBody.velocity.x = 0; duckBody.velocity.z = 0; }
      return;
    }

    // Camera forward/right projected onto the XZ ground plane.
    const cam = world.camera;
    _forward.set(0, 0, -1).applyQuaternion(cam.quaternion);
    _forward.y = 0;
    _forward.normalize();

    _right.set(1, 0, 0).applyQuaternion(cam.quaternion);
    _right.y = 0;
    _right.normalize();

    // Combine input with camera-relative directions.
    // y = forward (W/S), x = strafe (A/D) — per inputSystem convention.
    _direction.set(0, 0, 0);
    _direction.addScaledVector(_forward, y);
    _direction.addScaledVector(_right, x);
    _direction.normalize();

    // Drive the body via velocity so collisions with walls actually stop us.
    // Fallback: direct mesh move until physics has initialized.
    if (duckBody) {
      duckBody.velocity.x = _direction.x * DUCK.walkSpeed;
      duckBody.velocity.z = _direction.z * DUCK.walkSpeed;
      // physicsSystem.render copies x/z from body back to duck mesh.
    } else {
      world.duck.position.x += _direction.x * DUCK.walkSpeed * dt;
      world.duck.position.z += _direction.z * DUCK.walkSpeed * dt;
    }

    // Lerp rotation toward movement heading.
    // Duck faces +Z at rotation.y=0 (beak is at +Z in duck.ts).
    const targetAngle = Math.atan2(_direction.x, _direction.z);
    let diff = targetAngle - world.duck.rotation.y;
    // Shortest-path wrap to [-π, π].
    if (diff > Math.PI) diff -= Math.PI * 2;
    if (diff < -Math.PI) diff += Math.PI * 2;
    world.duck.rotation.y += diff * DUCK.turnLerp;
  },
};
